import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getProduct, updateProduct, deleteProduct, publishProduct, archiveProduct } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Save, Eye, Archive, Trash2, Share2 } from "lucide-react";
import ProductFiles from "@/pages/sites/ProductFiles";
import ProductLinks from "@/pages/sites/ProductLinks";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const ProductEdit: React.FC = () => {
  const { siteSlug, productId } = useParams<{ siteSlug: string; productId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<any>({});

  const { data: product, isLoading, error } = useQuery({
    queryKey: ["product", siteSlug, productId],
    queryFn: () => getProduct(siteSlug!, productId!),
    enabled: !!siteSlug && !!productId,
  });

  React.useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || "",
        description: product.description || "",
        amount: product.amount || "",
        content: product.content || "",
        receipt_button_text: product.receipt_button_text || "View content",
        receipt_custom_message: product.receipt_custom_message || "",
        limit_sales: product.limit_sales || false,
        allow_quantity_choice: product.allow_quantity_choice || false,
        show_sales_count: product.show_sales_count || false,
        is_e_publication: product.is_e_publication || false,
        require_shipping: product.require_shipping || false,
        refund_policy: product.refund_policy || "",
      });
    }
  }, [product]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => updateProduct(siteSlug!, productId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product", siteSlug, productId] });
      queryClient.invalidateQueries({ queryKey: ["products", siteSlug] });
      toast({
        title: "Product updated",
        description: "Your changes have been saved successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update product",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteProduct(siteSlug!, productId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products", siteSlug] });
      toast({
        title: "Product deleted",
        description: "The product has been deleted successfully.",
      });
      navigate(`/sites/${siteSlug}/products`);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete product",
        variant: "destructive",
      });
    },
  });

  const publishMutation = useMutation({
    mutationFn: () => publishProduct(siteSlug!, productId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product", siteSlug, productId] });
      queryClient.invalidateQueries({ queryKey: ["products", siteSlug] });
      toast({
        title: "Product published",
        description: "Your product is now live!",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to publish product",
        variant: "destructive",
      });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => archiveProduct(siteSlug!, productId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product", siteSlug, productId] });
      queryClient.invalidateQueries({ queryKey: ["products", siteSlug] });
      toast({
        title: "Product archived",
        description: "The product has been archived.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to archive product",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    updateMutation.mutate(formData);
  };

  const handleBack = () => {
    navigate(`/sites/${siteSlug}/products`);
  };

  const copyToClipboard = (text: string) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        toast({
          title: "Copied!",
          description: "URL copied to clipboard",
        });
      }).catch(() => {
        toast({
          title: "Failed to copy",
          description: "Please copy the URL manually",
          variant: "destructive",
        });
      });
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <Skeleton className="h-8 w-48 mb-6" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>
            Error loading product: {(error as Error).message}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!product) return null;

  const productUrl = `${window.location.origin}/sites/${siteSlug}/products/${productId}`;

  return (
    <div className="p-6 max-w-6xl- mx-auto-">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{product.name}</h1>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant={product.status === "published" ? "default" : "secondary"}>
                {product.status}
              </Badge>
              <span className="text-sm text-muted-foreground">
                {product.type?.replace("Product", "")}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {product.status === "draft" && (
            <Button onClick={() => publishMutation.mutate()} disabled={publishMutation.isPending}>
              <Eye className="mr-2 h-4 w-4" />
              Publish
            </Button>
          )}
          {product.status === "published" && (
            <Button variant="outline" onClick={() => archiveMutation.mutate()} disabled={archiveMutation.isPending}>
              <Archive className="mr-2 h-4 w-4" />
              Archive
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete the product.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteMutation.mutate()}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Tabs defaultValue="product" className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="product">Product</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="receipt">Receipt</TabsTrigger>
          <TabsTrigger value="share">Share</TabsTrigger>
        </TabsList>

        <TabsContent value="product">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="name">Product Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                  />
                </div>
                <div>
                  <Label htmlFor="amount">Price ($)</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="limit_sales"
                    checked={formData.limit_sales}
                    onCheckedChange={(checked) => setFormData({ ...formData, limit_sales: checked })}
                  />
                  <Label htmlFor="limit_sales">Limit product sales</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="allow_quantity_choice"
                    checked={formData.allow_quantity_choice}
                    onCheckedChange={(checked) => setFormData({ ...formData, allow_quantity_choice: checked })}
                  />
                  <Label htmlFor="allow_quantity_choice">Allow customers to choose a quantity</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="show_sales_count"
                    checked={formData.show_sales_count}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_sales_count: checked })}
                  />
                  <Label htmlFor="show_sales_count">Publicly show the number of sales</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_e_publication"
                    checked={formData.is_e_publication}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_e_publication: checked })}
                  />
                  <Label htmlFor="is_e_publication">Mark product as e-publication for VAT purposes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="require_shipping"
                    checked={formData.require_shipping}
                    onCheckedChange={(checked) => setFormData({ ...formData, require_shipping: checked })}
                  />
                  <Label htmlFor="require_shipping">Require shipping information</Label>
                </div>
                <div>
                  <Label htmlFor="refund_policy">Refund Policy</Label>
                  <Textarea
                    id="refund_policy"
                    value={formData.refund_policy}
                    onChange={(e) => setFormData({ ...formData, refund_policy: e.target.value })}
                    placeholder="Specify your refund policy..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="content">
          <Tabs defaultValue="content-text" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="content-text">Content Text</TabsTrigger>
              <TabsTrigger value="files">Files</TabsTrigger>
              <TabsTrigger value="links">Links</TabsTrigger>
            </TabsList>

            <TabsContent value="content-text" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Product Content</CardTitle>
                </CardHeader>
                <CardContent>
                  <Label htmlFor="content">Content</Label>
                  <Textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Enter product content, instructions, or details that customers will see after purchase..."
                    rows={15}
                    className="font-mono"
                  />
                  <p className="text-sm text-muted-foreground mt-2">
                    This content will be displayed to customers after they purchase the product.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="files" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Product Files</CardTitle>
                </CardHeader>
                <CardContent>
                  <ProductFiles site={{ slug: siteSlug! }} product={{ id: parseInt(productId!) }} />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="links" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Product Links</CardTitle>
                </CardHeader>
                <CardContent>
                  <ProductLinks site={{ slug: siteSlug! }} product={{ id: parseInt(productId!) }} />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="receipt">
          <Card>
            <CardHeader>
              <CardTitle>Receipt Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="receipt_button_text">Button text</Label>
                <Input
                  id="receipt_button_text"
                  value={formData.receipt_button_text}
                  onChange={(e) => setFormData({ ...formData, receipt_button_text: e.target.value })}
                  maxLength={26}
                  placeholder="View content"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Customize the download button text on receipts (max 26 characters).
                </p>
              </div>
              <div>
                <Label htmlFor="receipt_custom_message">Custom message</Label>
                <Textarea
                  id="receipt_custom_message"
                  value={formData.receipt_custom_message}
                  onChange={(e) => setFormData({ ...formData, receipt_custom_message: e.target.value })}
                  placeholder="Add a custom message to display on the receipt..."
                  rows={4}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  This message will be displayed on the customer's receipt after purchase.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="share">
          <Card>
            <CardHeader>
              <CardTitle>Share Your Product</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {product.status === "published" ? (
                <>
                  <div>
                    <Label htmlFor="product_url">Product URL</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        id="product_url"
                        value={productUrl}
                        readOnly
                        className="bg-muted"
                      />
                      <Button onClick={() => copyToClipboard(productUrl)}>
                        Copy
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label>Share on Social Media</Label>
                    <div className="flex gap-2 mt-2">
                      <Button
                        variant="outline"
                        onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(productUrl)}&text=${encodeURIComponent(product.name)}`, '_blank')}
                      >
                        <Share2 className="mr-2 h-4 w-4" />
                        Twitter
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productUrl)}`, '_blank')}
                      >
                        <Share2 className="mr-2 h-4 w-4" />
                        Facebook
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(productUrl)}`, '_blank')}
                      >
                        <Share2 className="mr-2 h-4 w-4" />
                        LinkedIn
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <Alert>
                  <AlertDescription>
                    Publish your product to start sharing it.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="mt-6 flex justify-end">
        <Button onClick={handleSubmit} disabled={updateMutation.isPending}>
          <Save className="mr-2 h-4 w-4" />
          {updateMutation.isPending ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};

export default ProductEdit;
